import React, { createContext, useState, useEffect, useCallback, ReactNode } from "react";
import { setAccessToken, getCsrf } from "../api";
import { authApi } from "../services/auth";
import { usersApi } from "../services/users";
import { ensureNativePushOnSignIn, unregisterNativePushForLogout } from "../lib/nativePush";
import { waitForPushAskMoment } from "../lib/firstSignInMoment";
import { setAppBadge } from "../lib/appBadge";
import NotificationsOffPopup from "../components/NotificationsOffPopup";
import { WALLET_MOMENT_EVENT } from "../lib/walletMoment";
import { resetAcceptingStatus } from "../lib/acceptingStatus";
import { resetLiveControlVisible } from "../lib/liveControlVisibility";
import { markExplicitSignOut } from "../lib/biometricSignIn";
import { pageCache } from "../lib/pageCache";
import { User } from "../types/User";

interface AuthContextProps {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (userData: User, accessToken: string) => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(null);
  // Every sign-in / sign-out hands the tab cache to that account BEFORE the
  // pages render with it: a different talent (or nobody) starts from an empty
  // cache, never from the previous talent's name, price or requests.
  const setUser = useCallback((next: User | null) => {
    pageCache.setOwner(next?._id || next?.id || null);
    setUserState(next);
  }, []);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        await getCsrf();

        // First check for a Stripe-return one-time token in the URL — left by
        // /stripe/connect's return_url. If present, exchange it for a fresh
        // access token + refresh cookie. This path works even when the browser
        // dropped the refresh cookie during the cross-site Stripe round-trip
        // (Safari ITP, third-party cookie restrictions, etc.) — keeping the
        // talent logged in after they finish bank onboarding.
        const params = new URLSearchParams(window.location.search);
        const stripeReturn = params.get("stripeReturn");
        if (stripeReturn) {
          // Strip the token from the URL up front so the page reads cleanly
          // and the (now-consumed) token doesn't sit in browser history.
          params.delete("stripeReturn");
          const cleanedQuery = params.toString();
          const cleanedUrl =
            window.location.pathname +
            (cleanedQuery ? `?${cleanedQuery}` : "") +
            window.location.hash;
          window.history.replaceState(null, "", cleanedUrl);

          try {
            const data = await authApi.exchangeStripeReturn(stripeReturn);
            setAccessToken(data.accessToken);
            setUser(data.user);
            setIsAuthenticated(true);

            // Mirror the pending_delete reactivation from the standard refresh
            // path so a talent who scheduled a deletion and then completed
            // Stripe Connect doesn't end up in a half-state.
            if (data.user?.status === "pending_delete") {
              try {
                await usersApi.cancelDeletion(data.user.slug);
                const refreshed = await authApi.user();
                setUser(refreshed);
              } catch {}
            }

            setLoading(false);
            return;
          } catch {
            // Exchange failed (token expired, replayed, etc.) — fall through
            // to the standard refresh-cookie path. The cookie may still be
            // intact, in which case the talent stays logged in regardless.
          }
        }

        const { accessToken } = await authApi.refreshToken();
        setAccessToken(accessToken);

        const userData = await authApi.user();
        setUser(userData);
        setIsAuthenticated(true);

        if (userData.status === "pending_delete") {
          try {
            await usersApi.cancelDeletion(userData.slug);
            const refreshed = await authApi.user();
            setUser(refreshed);
          } catch {}
        }
      } catch {
        setIsAuthenticated(false);
        setAccessToken(null);
        pageCache.setOwner(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [setUser]);

  // Native mobile push: push is ON by default — the moment
  // auth flips on (sign-in, post-Stripe registration, or a restored session on
  // app open), fire the one-shot OS permission prompt and register. The lib
  // respects the explicit choices we never override (per-device opt-out flag,
  // a previous "Don't Allow"). If they deny the prompt RIGHT NOW, show the
  // approved "Notifications are off" popup with the Settings deep link — Apple
  // never re-shows its own prompt. No-op on web.
  // ORDER (founder, build 38): Apple's prompt waits for the first-sign-in
  // moment to settle — after onboarding / the first-run tour AND after the
  // "Set up Face ID?" question (lib/firstSignInMoment; this provider sits
  // above the router, hence its cheap pathname poll). An install that already
  // allowed notifications never waits: it registers immediately, as before.
  const [showPushDeniedPopup, setShowPushDeniedPopup] = useState(false);
  const userId = user?._id;
  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    let cancelled = false;
    ensureNativePushOnSignIn(() => waitForPushAskMoment(userId, () => cancelled)).then((result) => {
      if (cancelled) return;
      // The ask is over → a new talent's Apple Wallet step may follow
      // (components/WalletMoment); after the "off" popup if they said no.
      if (result === "denied_prompt") setShowPushDeniedPopup(true);
      else window.dispatchEvent(new Event(WALLET_MOMENT_EVENT));
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userId]);

  const login = useCallback((userData: User, accessToken: string) => {
    setAccessToken(accessToken);
    setUser(userData);
    setIsAuthenticated(true);
    (async () => {
      if (userData?.status === "pending_delete") {
        try {
          await usersApi.cancelDeletion(userData.slug);
          const refreshed = await authApi.user();
          setUser(refreshed);
        } catch {}
      }
    })();
  }, [setUser]);

  const logout = useCallback(async () => {
    // An explicit "Log out" must land on a calm login page: the Face ID
    // auto-prompt stays quiet until the app is reopened (the button remains).
    // Face ID keys themselves survive sign-out by design (lib/biometricSignIn).
    markExplicitSignOut();
    // Drop this device's native push token first (while still authenticated) so
    // the next person to log in on a shared phone can't receive this DJ's
    // request alerts. No-op on web/desktop; preference is untouched so logging
    // back in re-registers normally.
    try { await unregisterNativePushForLogout(); } catch {}
    // A signed-out phone must not keep advertising the previous talent's
    // pending-request count on the app icon.
    try { await setAppBadge(0); } catch {}
    try { await authApi.logout(); } catch {}
    setAccessToken(null);
    setUser(null);
    setIsAuthenticated(false);
    // Clear the shared Live/Offline stores so the next talent on a shared
    // device (SPA logout→login never reloads the page) starts from their OWN
    // fetched state instead of inheriting this talent's cached pill.
    resetAcceptingStatus();
    resetLiveControlVisible();
  }, [setUser]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, logout }}>
      {children}
      <NotificationsOffPopup
        open={showPushDeniedPopup}
        onClose={() => {
          setShowPushDeniedPopup(false);
          window.dispatchEvent(new Event(WALLET_MOMENT_EVENT));
        }}
      />
    </AuthContext.Provider>
  );
};
